import React, { useMemo, useContext } from 'react';
import { Typography, ConfigProvider } from 'antd';
import type { TextProps } from 'antd/es/typography/Text';
import { useAndStore } from '../../base/store/useAndStore';
import { parseClickAction, createCommand } from '../../base/actionParser';
import { DataSetInheritContext, useResolvedDataSet } from '../../base/DataSetInheritContext';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseArrayNotation } from '../../base/store/storePathUtils';
import { useDictOptions } from '../../base/hooks/useApiService';
import { findDictOptionByValue } from '../../config';
import { formatByType, type FormatType, type FormatOptions } from '../../utils/ReactFormatter';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { useResolveTokenColor } from '../../utils/resolveTokenColor';
import { deriveScopedComponentId } from '../../base/storePropsUtils';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

const { Text } = Typography;

/**
 * AndText Props 接口
 */
export interface AndTextProps extends TextProps, BaseComponentProps, DataSetConfig {
  /** 文本内容 */
  content?: string;
  /**
   * 迭代器 / Masonry 单元下标：参与 clickAction 占位解析；与 DataSetInheritContext 一并传入
   * `deriveScopedComponentId`，与 AndRadio 及 Extend `setProps` 键一致。
   */
  index?: number;
  /** 点击动作 */
  clickAction?: string;
  /** 是否阻止点击冒泡；未设置时，若存在 clickAction 则默认 true，避免外层行/容器 onClick 覆盖子区域动作 */
  stopClickPropagation?: boolean;
  /** 字体大小 */
  fontSize?: number | string;
  /** 字体颜色 */
  color?: string;
  /** 字典编码 - 用于将 value 转换为 label 显示 */
  dictCode?: string;
  /** 格式化类型 */
  formatType?: FormatType | string;
  /** 格式化参数 */
  formatValue?: FormatOptions | string;
  /** 当值为空时是否显示 content 作为缺省值 */
  fallbackDisplay?: boolean;
  /** 组件样式配置 */
  styles?: React.CSSProperties;
  /** Design Token（通过 ConfigProvider theme.token 方式生效） */
  token?: Record<string, any> | string;
}

/**
 * AndText 组件
 * 封装 Ant Design Typography.Text，支持数据绑定、字典转换和数据格式化
 */
export const AndText: React.FC<AndTextProps> = ({
  id,
  sessionId,
  develop,
  content,
  children,
  dataSet,
  dataSetField,
  fontSize,
  color,
  dictCode,
  formatType,
  formatValue,
  fallbackDisplay = false,
  styles,
  token,
  // Filter out custom props that shouldn't be passed to DOM
  clickAction,
  stopClickPropagation,
  index,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;
  const hasExplicitDataSet = typeof dataSet === 'string' && dataSet.trim() !== '';
  const hasDataField = typeof dataSetField === 'string' && dataSetField.trim() !== '';
  const shouldBindDataSet = hasExplicitDataSet || hasDataField || dictCode === 'selectedCount';
  const resolved = useResolvedDataSet(
    shouldBindDataSet ? dataSet : undefined,
    shouldBindDataSet ? dataSetField : undefined,
    sessionId
  );
  const effectiveSessionId = sessionId ?? resolved.sessionId;
  const getDataSet = useAndStore((state) => state.getDataSet);

  // propsStore 键与 AndRadio / form-fill Extend.setProps 一致（含表格 Masonry 双维）
  const derivedPropsComponentId = useMemo(
    () => (effectiveSessionId ? deriveScopedComponentId(id, ctx, index) : null),
    [effectiveSessionId, id, ctx?.parentIndex, ctx?.index, index]
  );

  const storeProps = useAndStore((state) =>
    derivedPropsComponentId
      ? state.propsStore[`${effectiveSessionId}_props`]?.[derivedPropsComponentId] ?? null
      : null
  );

  /** propsStore 可覆盖（与 form-fill Extend.setProps 等一致） */
  const effectiveFallbackDisplay = useMemo(
    () => storeProps?.fallbackDisplay ?? fallbackDisplay,
    [storeProps?.fallbackDisplay, fallbackDisplay]
  );

  // dictCode=selectedCount 时：用 dataSetField 作为 dataSet，读取 selectedCount 字段（表格选中数展示）
  const effectiveDataSet = useMemo(
    () => {
      if (dictCode === 'selectedCount' && dataSetField && !dataSet) {
        return dataSetField;
      }
      return shouldBindDataSet ? resolved.dataSet : undefined;
    },
    [dictCode, dataSetField, dataSet, resolved.dataSet, shouldBindDataSet]
  );
  const effectiveDataSetField = useMemo(
    () => {
      if (dictCode === 'selectedCount') return 'selectedCount';
      return shouldBindDataSet ? resolved.dataSetField : undefined;
    },
    [dictCode, resolved.dataSetField, shouldBindDataSet]
  );

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );

  // 订阅 dataSets 对应块，使 store 更新时触发重渲染（与 useStoreData 一致）
  const dataChunk = useAndStore((s) => {
    if (!dataSetKey) return undefined;
    const storeKey = parseArrayNotation(dataSetKey).baseName;
    return s.dataSets[storeKey];
  });

  // 使用 dictCode 加载字典数据
  const { options: dictOptions } = useDictOptions(dictCode);

  // 从数据集获取值（依赖 dataChunk 以响应 store 变化）
  const storeValue = useMemo(() => {
    if (dataSetKey && effectiveDataSetField) {
      return getDataSet(dataSetKey, effectiveDataSetField);
    }
    return undefined;
  }, [dataSetKey, effectiveDataSetField, getDataSet, dataChunk]);

  // 解析格式化参数
  const formatOptions = useMemo((): FormatOptions => {
    if (!formatValue) return {};
    if (typeof formatValue === 'string') {
      try {
        return JSON.parse(formatValue);
      } catch {
        return {};
      }
    }
    return formatValue;
  }, [formatValue]);

  // 检查是否为空值
  const isEmpty = (value: any): boolean => {
    return value === null || value === undefined || value === '' || 
           (typeof value === 'string' && value.trim() === '');
  };

  /** 运行时已从 dataSet 取数：空字符串 / null 等也视为 store 结果，不再回退静态 content（除非 fallbackDisplay） */
  const isDataSetBoundForText = useMemo(
    () =>
      shouldBindDataSet &&
      Boolean(
        dataSetKey &&
          effectiveDataSetField &&
          String(effectiveDataSetField).trim() !== ''
      ),
    [shouldBindDataSet, dataSetKey, effectiveDataSetField]
  );

  // 获取原始值
  const rawValue = useMemo(() => {
    // 设计模式下，直接显示 content
    if (develop) {
      return content;
    }
    if (isDataSetBoundForText) {
      if (effectiveFallbackDisplay && isEmpty(storeValue)) {
        if (storeProps?.content !== undefined && storeProps?.content !== null) {
          return storeProps.content;
        }
        return content;
      }
      return storeValue;
    }
    // 其次使用 storeProps 中的 content
    if (storeProps?.content !== undefined) {
      return storeProps.content;
    }
    // 最后使用 prop 中的值
    return content;
  }, [
    develop,
    isDataSetBoundForText,
    storeValue,
    storeProps?.content,
    content,
    effectiveFallbackDisplay,
  ]);

  // 字典转换：将 value 转换为 label
  const dictTransformed = useMemo(() => {
    if (dictCode && dictOptions.length > 0 && rawValue !== undefined && rawValue !== null) {
      const item = findDictOptionByValue(dictOptions, rawValue);
      return item?.label ?? rawValue;
    }
    return rawValue;
  }, [dictCode, dictOptions, rawValue]);

  // 格式化处理：应用 formatType
  const displayContent = useMemo(() => {
    // 设计模式下跳过格式化
    if (develop) {
      return dictTransformed;
    }
    
    // 获取有效的格式化类型
    const effectiveFormatType = storeProps?.formatType || formatType;
    
    if (effectiveFormatType && dictTransformed !== undefined && dictTransformed !== null) {
      return formatByType(dictTransformed, effectiveFormatType, formatOptions);
    }
    return dictTransformed;
  }, [develop, dictTransformed, formatType, formatOptions, storeProps?.formatType]);

  // 合并 props（排除不传给 DOM 的自定义 prop，避免 React 警告）
  const mergedProps = useMemo(() => {
    const baseProps = { ...restProps };
    if (storeProps && typeof storeProps === 'object') {
      Object.keys(storeProps).forEach((key) => {
        if (
          ![
            'content',
            'formatType',
            'formatValue',
            'clickAction',
            'stopClickPropagation',
            'fallbackDisplay',
            'defaultShow',
          ].includes(key) &&
          storeProps[key] !== undefined &&
          storeProps[key] !== null
        ) {
          (baseProps as any)[key] = storeProps[key];
        }
      });
    }
    return baseProps;
  }, [restProps, storeProps]);

  const textDomProps = useMemo(
    () => prepareAntdDomProps(mergedProps as Record<string, unknown>),
    [mergedProps],
  );

  const effectiveClickAction = useMemo(
    () => storeProps?.clickAction ?? clickAction,
    [storeProps?.clickAction, clickAction]
  );

  const effectiveDevelop = storeProps?.develop ?? develop;

  const shouldStopPropagation = useMemo(() => {
    // 画布/develop：需冒泡以便 GrapesJS 选中组件
    if (effectiveDevelop) return false;
    const effectiveStop = storeProps?.stopClickPropagation ?? stopClickPropagation;
    if (effectiveStop !== undefined) return effectiveStop;
    return Boolean(effectiveClickAction);
  }, [
    effectiveDevelop,
    storeProps?.stopClickPropagation,
    stopClickPropagation,
    effectiveClickAction,
  ]);

  // 解析 Design Token 颜色
  const effectiveColor = storeProps?.color ?? color;
  const resolvedColor = useResolveTokenColor(effectiveColor);

  const filteredToken = useMemo(() => {
    if (!token) return undefined;
    let raw: any = token;
    if (typeof token === 'string') {
      try {
        raw = JSON.parse(token);
      } catch {
        return undefined;
      }
    }
    if (!raw || typeof raw !== 'object') return undefined;
    const result: Record<string, any> = {};
    Object.entries(raw).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null) result[key] = value;
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }, [token]);

  // 自定义样式
  const customStyle = useMemo(() => {
    const style: React.CSSProperties = { width: '100%' };
    const fs = storeProps?.fontSize ?? fontSize;
    
    if (fs) {
      // 如果没有单位，默认添加 px
      const formattedFontSize = fs && !String(fs).match(/\d+(px|em|rem|%|vh|vw|pt|pc|in|cm|mm|ex|ch)$/)
        ? `${fs}px`
        : fs;
      style.fontSize = formattedFontSize;
    }
    if (resolvedColor) {
      style.color = resolvedColor;
    }
    return style;
  }, [fontSize, resolvedColor, storeProps?.fontSize]);

  const textElement = (
    <Text
      {...textDomProps}
      style={{ ...customStyle, ...styles, ...(textDomProps.style as React.CSSProperties) }}
      onClick={(e) => {
        // 先执行外部 onClick，保持组件可扩展性
        if (typeof mergedProps.onClick === 'function') {
          mergedProps.onClick(e);
        }
        // 再执行 clickAction 分发，保持与 AndButton 行为一致
        if (effectiveClickAction && effectiveSessionId) {
          const { actionName, arg } = parseClickAction(
            effectiveClickAction,
            {
              id,
              index: effectiveIndex,
              parentIndex: effectiveParentIndex,
              dataSet: effectiveDataSet,
              dataSetField: effectiveDataSetField,
              ...mergedProps,
            },
            ctx
          );
          const command = createCommand(actionName, arg);
          addCommand(effectiveSessionId, command);
        }
        if (shouldStopPropagation) {
          e.stopPropagation();
        }
      }}
    >
      {displayContent !== undefined && displayContent !== null && displayContent !== '' 
        ? String(displayContent) 
        : children}
    </Text>
  );

  return filteredToken ? (
    <ConfigProvider theme={{ token: filteredToken }}>{textElement}</ConfigProvider>
  ) : (
    textElement
  );
};

export default AndText;


